import { CultivationCyclePopulatedSchema, PhenologicalStateWithCycle } from '../../../requests/api/apiTypes';

// Color constants for better maintainability
const KC_COLORS = {
  RED: '#BF3F23',
  BLUE: '#2196F3',
  GREEN: '#6FBF23',
} as const;

const ROOTS_COLORS = {
  YELLOW: '#FFD700',
  GREEN: '#6FBF23',
  BLUE: '#2196F3',
} as const;

// Paleta para bandas de estados fenológicos. `fill` = fondo translúcido, `solid` = borde/chip.
// Se asigna por orden global de estados (mismo criterio que el PhenologicalTimeline).
export const PHENOLOGY_PALETTE = [
  { fill: 'rgba(59,130,246,0.22)', solid: 'rgb(59,130,246)' }, // azul
  { fill: 'rgba(16,185,129,0.22)', solid: 'rgb(16,185,129)' }, // verde
  { fill: 'rgba(245,158,11,0.22)', solid: 'rgb(245,158,11)' }, // ámbar
  { fill: 'rgba(236,72,153,0.22)', solid: 'rgb(236,72,153)' }, // rosa
  { fill: 'rgba(139,92,246,0.22)', solid: 'rgb(139,92,246)' }, // violeta
  { fill: 'rgba(14,165,233,0.22)', solid: 'rgb(14,165,233)' }, // cyan
  { fill: 'rgba(220,38,38,0.22)', solid: 'rgb(220,38,38)' }, // rojo
];

const DEFAULT_KC_THRESHOLDS = {
  LOW: 0.5,
  MEDIUM: 0.6,
} as const;

export const getRootsColor = (rootsDepth: number, irrigationDepth: number): string => {
  const roundedRoots = Math.round(rootsDepth * 10) / 10;
  const roundedIrrigation = Math.round(irrigationDepth * 10) / 10;

  if (roundedRoots > roundedIrrigation) {
    return ROOTS_COLORS.YELLOW;
  }
  if (roundedRoots === roundedIrrigation) {
    return ROOTS_COLORS.GREEN;
  }
  return ROOTS_COLORS.BLUE;
};

export const getDefaultKcColor = (kcValue: number): string => {
  if (kcValue < DEFAULT_KC_THRESHOLDS.LOW) return KC_COLORS.RED;
  if (kcValue < DEFAULT_KC_THRESHOLDS.MEDIUM) return KC_COLORS.BLUE;
  return KC_COLORS.GREEN;
};

const findApplicablePhenologicalState = (
  cycleStates: any[],
  date: string,
): any | null => {
  const currentDate = new Date(date);

  return cycleStates.find((state) => {
    const stateStartDate = new Date(state.startDate);
    const stateEndDate = new Date(state.endDate);
    return currentDate >= stateStartDate && currentDate <= stateEndDate;
  }) || null;
};

const hasValidKcRanges = (state: any): boolean => state.kcMin !== null
  && state.kcMin !== undefined
  && state.kcMax !== null
  && state.kcMax !== undefined;

const getKcColorByRanges = (kcValue: number, minKc: number, maxKc: number): string => {
  if (kcValue < minKc) {
    return KC_COLORS.RED;
  }
  if (kcValue >= minKc && kcValue <= maxKc) {
    return KC_COLORS.GREEN;
  }
  return KC_COLORS.BLUE;
};

export const getKcColor = (
  kcValue: number,
  date?: string,
  selectedCycle?: CultivationCyclePopulatedSchema | null,
  phenologicalStatesByCycle?: PhenologicalStateWithCycle,
): string => {
  if (!selectedCycle || !phenologicalStatesByCycle || !date) {
    return getDefaultKcColor(kcValue);
  }

  const cycleStates = phenologicalStatesByCycle[selectedCycle.id];
  if (!cycleStates || cycleStates.length === 0) {
    return getDefaultKcColor(kcValue);
  }

  const applicableState = findApplicablePhenologicalState(cycleStates, date);

  if (applicableState && hasValidKcRanges(applicableState)) {
    return getKcColorByRanges(kcValue, applicableState.kcMin, applicableState.kcMax);
  }

  return getDefaultKcColor(kcValue);
};
